import { useHitTarget } from '../responsive/hitTarget'
import './switch.css'

type Props = {
  checked: boolean
  onChange: (next: boolean) => void
  /** The accessible name. */
  label: string
  disabled?: boolean
  describedBy?: string
}

/**
 * An on/off setting that takes effect when flipped: a real
 * `<input type="checkbox" role="switch">`, invisible but stretched over the
 * hit target, with the visible track drawn by a sibling (the `Checkbox`
 * construction, so every pixel is a class in `switch.css`).
 *
 * **Controlled, and never flips itself.** `onChange` reports the opposite of
 * `checked`; a caller that must confirm first (turning a setting off that
 * forgets data) simply does not change `checked`, and the control stays put.
 * **The state is never colour alone**: the thumb sits at the end when on.
 */
export function Switch({ checked, onChange, label, disabled, describedBy }: Props) {
  const target = useHitTarget('page')
  return (
    <span className="switch" style={{ minHeight: target, minWidth: target }}>
      <input
        type="checkbox"
        role="switch"
        className="switch__input"
        checked={checked}
        aria-label={label}
        aria-describedby={describedBy}
        disabled={disabled}
        onChange={() => onChange(!checked)}
      />
      <span className={`switch__track${checked ? ' switch__track--on' : ''}`} aria-hidden="true">
        <span className={`switch__thumb${checked ? ' switch__thumb--on' : ''}`} />
      </span>
    </span>
  )
}
